import React, { useState, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { LogOut, Menu, X, LayoutDashboard, FileText, Briefcase, CheckSquare, GanttChart, BarChart3, Settings, ChevronDown, User, Moon, Sun, Users } from 'lucide-react';
import NotificationDropdown from './NotificationDropdown';
import UserProfileModal from './UserProfileModal';
import UniversalSearch from './UniversalSearch';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import { MiraNavTrigger } from './MiraFeedbackWidget';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { user, logout, isAdmin, isDirector } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useKeyboardShortcuts({
    onFocusSearch: () => searchRef.current?.focus(),
    onToggleHelp: () => setShowShortcutsHelp(prev => !prev),
  });

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Contracts', href: '/contracts', icon: FileText },
    { name: 'Projects', href: '/projects', icon: Briefcase },
    { name: 'Tasks', href: '/tasks', icon: CheckSquare },
    { name: 'Workforce (Gantt)', href: '/workforce', icon: GanttChart },
    { name: 'Workload (Heatmap)', href: '/workload', icon: BarChart3 },
    { name: 'Employee Capacity', href: '/employees/capacity', icon: Users },
  ];

  if (isAdmin || isDirector) {
    navigation.push({ name: 'Admin', href: '/admin', icon: Settings });
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'ADMIN': return { bg: '#FEE2E2', text: '#DC2626' };
      case 'DIRECTOR': return { bg: '#F3E8FF', text: '#7C3AED' };
      case 'LEAD': return { bg: '#E8F0F7', text: '#2B5C82' };
      default: return { bg: '#DEF7E9', text: '#3E7B58' };
    }
  };

  const roleColors = getRoleBadgeColor(user?.role);

  return (
    <div className="min-h-screen bg-wmt-primary" >      {/* Top Bar */}
      <div className="h-16 border-b shadow-sm bg-wmt-secondary border-wmt-primary" >        <div className="h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden p-2 rounded-lg transition"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <img 
              src="https://customer-assets.emergentagent.com/job_101bb3ff-a91b-44b5-9655-2a8dbd26c2f1/artifacts/dknvzqxd_alliance-branding-min-red.png" 
              alt="Alliance Logo" 
              className="h-9 w-auto ml-6"
            />
          </div>
          
          <div className="flex items-center gap-2">
            {/* Universal Search */}
            <UniversalSearch searchInputRef={searchRef} />
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg transition"
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Mira — in-app feedback trigger. Drawer mounts at App root. */}
            <MiraNavTrigger />

            {/* Notifications */}
            <NotificationDropdown />
            
            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition"
                data-testid="user-menu-button"
              >
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                >
                  {user?.name?.split(' ').map(n => n[0]).join('') || '?'}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-wmt-primary" >{user?.name}</p>                  <p className="text-xs text-wmt-muted" >{user?.role}</p>                </div>
                <ChevronDown size={16}  className="hidden md:block text-wmt-muted" />              </button>

              {/* User Dropdown */}
              {showUserMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-40"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div 
                    className="absolute right-0 mt-2 w-64 rounded-xl shadow-2xl overflow-hidden z-50 bg-wmt-secondary"
                    
                  >
                    {/* User Info Header */}
                    <div className="p-4 border-b border-wmt-primary bg-wmt-tertiary" >                      <div className="flex items-center gap-3">
                        <div 
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${darkMode ? 'bg-blue-500' : 'bg-[#2B5C82]'}`}
                        >
                          {user?.name?.split(' ').map(n => n[0]).join('') || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-wmt-primary" >{user?.name}</p>                          <p className="text-xs text-wmt-muted" >{user?.email}</p>                        </div>
                      </div>
                      <span 
                        className="badge-status inline-block mt-2 px-2 py-0.5 rounded text-xs font-semibold"
                        style={{ '--_bg': roleColors.bg, '--_text': roleColors.text } as React.CSSProperties}
                      >
                        {user?.role}
                      </span>
                    </div>

                    {/* Menu Items */}
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          setShowProfileModal(true);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition text-left"
                      >
                        <User size={18} />
                        <div>
                          <p className="text-sm font-medium text-wmt-primary" >Profile & Settings</p>                          <p className="text-xs text-wmt-muted" >Manage your account</p>                        </div>
                      </button>
                    </div>

                    {/* Logout */}
                    <div className="p-2 border-t border-wmt-primary" >                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition text-left"
                        data-testid="logout-button"
                      >
                        <LogOut size={18} />
                        <span className="text-sm font-medium">Sign Out</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-4rem)]">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'block' : 'hidden'
          } md:block w-64 border-r flex-shrink-0`}
        >
          <nav className="p-4 space-y-1 sticky top-0 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition"
                  style={{
                    background: isActive ? 'var(--bg-accent)' : 'transparent',
                    color: isActive ? 'var(--text-secondary)' : 'var(--text-secondary)',
                  }}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon size={18} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-y-auto overflow-x-hidden">
          {children}
        </main>
      </div>

      {/* Profile Modal */}
      <UserProfileModal 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onLogout={handleLogout}
      />

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp open={showShortcutsHelp} onClose={() => setShowShortcutsHelp(false)} />
    </div>
  );
};

export default Layout;
